import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  RoleDto,
  RoleListSmallDto,
} from '../../../role-list/models/role-models';
import { StatusList } from '../add-user-modal/add-user-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../../services/modal.service';
import { EditUserDto, UserInfoDto } from '../../models/user-models';
import { Subject } from 'rxjs';
import { fullNameValidator } from '../../../../validators/fullName.validator';
import { UserDataService } from '../../services/user-data.service';
import { UserInfoService } from 'src/app/services/user-info.service';

@Component({
  selector: 'app-edit-user-modal',
  templateUrl: './edit-user-modal.component.html',
  styleUrls: [
    './edit-user-modal.component.scss',
    '../add-user-modal/add-user-modal.component.scss',
  ],
})
export class EditUserModalComponent implements OnInit {
  @Input() roleList: RoleListSmallDto[] = [];
  @Input() userInfo: Subject<UserInfoDto> | undefined;

  @Output() editedUserEvent: EventEmitter<number> = new EventEmitter<number>();

  userInfoDetail: UserInfoDto | undefined;

  form!: FormGroup;

  isOpenStatusSelect = false;
  isOpenRoleSelect = false;
  selectedStatus: boolean | undefined;
  selectedRole: number[] = [];
  twoFactor = false;

  userId!: number;
  currentUserId?: number;

  isSending = false;

  statusList: StatusList[] = [
    { name: 'ACTIVE', state: true },
    { name: 'BLOCKED', state: false },
  ];

  @HostListener('document:click', ['$event'])
  onClick(event: Event) {
    if (this.el.nativeElement.contains(event.target)) {
      this.closeAllSelectors();
    }
  }

  constructor(
    private el: ElementRef,
    private translateService: TranslateService,
    public modalService: ModalService,
    private userDataService: UserDataService,
    private userInfoService: UserInfoService
  ) {}

  ngOnInit() {
    this.getCurrentUserId();

    this.initForm();

    this.userInfo?.subscribe({
      next: (res) => {
        this.userInfoDetail = res;
        this.userId = res.id;
        this.selectedStatus = res.is_enabled;

        if (res.is_enabled) {
          this.setStatus({ name: 'ACTIVE', state: true });
        } else {
          this.setStatus({ name: 'BLOCKED', state: false });
        }

        this.roleList.forEach((item) => {
          if (item.name === res.role_name) {
            this.setRole(item);
          }
        });

        this.twoFactor = res.two_factor_auth;

        this.form.patchValue({phoneFormControl: this.userInfoDetail?.phone_number.substring(2),});
        this.form.patchValue({fullNameFormControl: this.userInfoDetail?.full_name,});

        this.closeAllSelectors();

        this.currentUserId === this.userId
          ? this.form.controls['roleFormControl'].disable()
          : this.form.controls['roleFormControl'].enable();
      },
    });
  }

  initForm(): void {
    this.form = new FormGroup({
      fullNameFormControl: new FormControl('', [
        Validators.required,
        fullNameValidator(),
      ]),
      phoneFormControl: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
      ]),
      statusFormControl: new FormControl('', [Validators.required]),
      roleFormControl: new FormControl('', [Validators.required]),
    });
  }

  get fullNameRequireError() {
    return this.form.get('fullNameFormControl')?.hasError('required');
  }

  get fullNameFormatError() {
    return this.form.get('fullNameFormControl')?.hasError('fullNameNotValid');
  }

  get phoneFormatError() {
    return this.form.get('phoneFormControl')?.hasError('minlength');
  }

  getCurrentUserId() {
    this.userInfoService.userId$.subscribe({
      next: (res) => {
        this.currentUserId = res;
      },
    });
  }

  openStatusSelect(event: MouseEvent) {
    event.stopPropagation();
    this.closeAllSelectors();
    this.isOpenStatusSelect = true;
  }

  openRoleSelect(event: MouseEvent) {
    event.stopPropagation();
    this.closeAllSelectors();
    this.isOpenRoleSelect = true;
  }

  setStatus(item: StatusList) {
    this.selectedStatus = item.state;
    this.translateService.get(`USER_LIST.MODAL.${item.name}`).subscribe({
      next: (res) => {
        this.form.patchValue({ statusFormControl: res });
      },
    });
  }

  closeAllSelectors() {
    this.isOpenStatusSelect = false;
    this.isOpenRoleSelect = false;
  }

  setRole(role: RoleListSmallDto) {
    this.selectedRole = [];
    this.selectedRole.push(role.id);
    this.form.patchValue({ roleFormControl: role.name });
  }

  sendForm() {
    this.isSending = true;
    const userData: EditUserDto = {
      full_name: this.form.get('fullNameFormControl')?.value,
      phone_number: `+7${this.form.get('phoneFormControl')?.value}`,
      is_enabled: this.selectedStatus,
      two_factor_auth: this.twoFactor,
      role_id: this.selectedRole.join(),
    };
    this.userDataService.editUser(this.userId, userData).subscribe({
      next: () => {
        this.modalService.isModalOpen$.next(false);
        this.editedUserEvent.next(this.userId);
        this.isSending = false;
      },
    });
  }
}
